import React, { useMemo, useState, useEffect } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Modal, Select, DatePicker, Button, Spin } from "antd";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ImageWithBasePath from "../../../core/img/imagewithbasebath";
import Table from "../../../core/pagination/Newdatatable";
import GRAN_LOGO1 from "../Logo/Granules_logo1.png";
import { getApiBaseUrl, ADD_ACTIVITY, LOGIN } from "../Config/Config";
import dayjs from "dayjs"; 

const { Option } = Select;
const { RangePicker } = DatePicker;

const AuditLogReport = () => {
  const [visible, setVisible] = useState(false);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [downloadReportLoading, setDownloadReportLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [dataSource, setDataSource] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(0);

  // Use dayjs for RangePicker compatibility
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(7, "day"),
    dayjs(),
  ]);

  const [users, setUsers] = useState([]);
  const [showPassword, setShowPassword] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalRecords, setTotalRecords] = useState(0);

  const user = JSON.parse(sessionStorage.getItem("user")) || {
    UserName: "Unknown User",
    UserID: null,
  };

  const reportConfig = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");

    return {
      title: "Audit Trial Report",
      filename: `AuditTrialReport_${year}-${month}-${day}_${hours}-${minutes}-${seconds}`,
      columns: [
        {
          title: "Date & Time",
          key: "PerformedOn",
          render: (text) => {
            if (!text) return "NA";
            const date = new Date(text);
            if (isNaN(date.getTime())) return "NA";
            const d = String(date.getUTCDate()).padStart(2, "0");
            const m = String(date.getUTCMonth() + 1).padStart(2, "0");
            const y = date.getUTCFullYear();
            const h = String(date.getUTCHours()).padStart(2, "0");
            const min = String(date.getUTCMinutes()).padStart(2, "0");
            const formatted = `${d}/${m}/${y} ${h}:${min}`;
            return formatted.startsWith("01/01/1900") ? "NA" : formatted;
          },
        },
        { title: "User ID", key: "username" },
        {
          title: "Description",
          key: "Notes",
          render: (text) => {
            if (!text) return "";
            let result = "";
            let line = "";
            text.split(" ").forEach((word) => {
              if ((line + word).length > 50) {
                result += line.trim() + "\n";
                line = "";
              }
              line += word + " ";
            });
            return <span style={{ whiteSpace: "pre-line" }}>{(result + line).trim()}</span>;
          },
        },
        {
          title: "Status",
          key: "IsSucces",
          render: (value) => (value ? "Success" : "Failed"),
        },
      ],
    };
  }, []);

  const formatDateForAPI = (dayjsObj) => {
    return dayjsObj.format("YYYY-MM-DD HH:mm:ss");
  };

  const fetchAuditLogData = async (page = 1, limit = 100) => {
    setLoading(true);
    try {
      const startDate = formatDateForAPI(dateRange[0]);
      const endDate = formatDateForAPI(dateRange[1]);

      const url = `${getApiBaseUrl()}/filterph2/getAuditReportpg?PerformedBy=${selectedUserId}&startdate=${encodeURIComponent(
        startDate
      )}&enddate=${encodeURIComponent(endDate)}&pageNumber=${page}&recordslimit=${limit}`;

      const response = await fetch(url);
      const data = await response.json();

      if (data.Status && Array.isArray(data.ResultData)) {
        const list = data.ResultData || [];
        setDataSource(list);
        if (list.length > 0 && list[0].TotalCount !== undefined) {
          setTotalRecords(list[0].TotalCount);
        } else {
          setTotalRecords(0);
        }
      } else {
        setDataSource([]);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error("Error fetching audit log data:", error);
      setDataSource([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllAuditLogDataForPDF = async () => {
    if (totalRecords === 0) return [];

    try {
      const startDate = formatDateForAPI(dateRange[0]);
      const endDate = formatDateForAPI(dateRange[1]);

      const url = `${getApiBaseUrl()}/filterph2/getAuditReportpg?PerformedBy=${selectedUserId}&startdate=${encodeURIComponent(
        startDate
      )}&enddate=${encodeURIComponent(endDate)}&pageNumber=1&recordslimit=${totalRecords}`;

      const response = await fetch(url);
      const data = await response.json();

      if (data.Status && Array.isArray(data.ResultData)) {
        return data.ResultData || [];
      }
      return [];
    } catch (error) {
      console.error("Error fetching full data for PDF:", error);
      return [];
    }
  };

  useEffect(() => {
    fetch(`${getApiBaseUrl()}/api/getAllUsers`)
      .then((res) => res.json())
      .then((data) => setUsers(data?.ResultData || []))
      .catch(() => setUsers([]));
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    fetchAuditLogData(1, pageSize);
  }, [selectedUserId, dateRange]);

  useEffect(() => {
    fetchAuditLogData(currentPage, pageSize);
  }, [currentPage, pageSize]);

  const tableColumns = useMemo(
    () =>
      reportConfig.columns.map((col) => ({
        title: col.title,
        dataIndex: col.key,
        key: col.key,
        render: col.render || ((text) => text || "-"),
        sorter: (a, b) => {
          const valA = a[col.key] ?? "";
          const valB = b[col.key] ?? "";
          return typeof valA === "number"
            ? valA - valB
            : String(valA).localeCompare(String(valB));
        },
      })),
    [reportConfig]
  );

  const renderTooltip = (props) => (
    <Tooltip id="pdf-tooltip" {...props}>
      Pdf
    </Tooltip>
  );

  const handleExportPDF = async () => {
    setDownloadReportLoading(true);

    const fullData = await fetchAllAuditLogDataForPDF();

    const doc = new jsPDF();
    const logoWidth = 15;
    const logoHeight = 15;
    const pageWidth = doc.internal.pageSize.getWidth();

    const generateHeader = () => {
      doc.addImage(GRAN_LOGO1, "PNG", 10, 10, logoWidth, logoHeight);
      doc.setFontSize(11);
      doc.text("Granules India Limited", 30, 14);
      doc.text("Gagillapur", 30, 20);
      doc.setFontSize(10);
      doc.setFont(undefined, "bold");
      doc.text(`Title: ${reportConfig.title}`, 10, 35);
      doc.setFont(undefined, "normal");
      return 42;
    };

    const startY = generateHeader();

    autoTable(doc, {
      startY: startY,
      head: [reportConfig.columns.map((col) => col.title)],
      body:
        fullData.length > 0
          ? fullData.map((item) =>
              reportConfig.columns.map((col) => {
                if (col.key === "Notes") {
                  const notes = item[col.key] || "";
                  let result = "";
                  let line = "";
                  notes.split(" ").forEach((word) => {
                    if ((line + word).length > 50) {
                      result += line.trim() + "\n";
                      line = "";
                    }
                    line += word + " ";
                  });
                  return (result + line).trim();
                } else if (col.key === "PerformedOn") {
                  const date = new Date(item[col.key]);
                  if (isNaN(date.getTime())) return "NA";
                  return `${String(date.getUTCDate()).padStart(2, "0")}/${String(
                    date.getUTCMonth() + 1
                  ).padStart(2, "0")}/${date.getUTCFullYear()} ${String(
                    date.getUTCHours()
                  ).padStart(2, "0")}:${String(date.getUTCMinutes()).padStart(2, "0")}`;
                } else {
                  return col.render
                    ? col.render(item[col.key])
                    : item[col.key] ?? "";
                }
              })
            )
          : [["No Data Available"]],
      theme: "grid",
      headStyles: { fillColor: [22, 160, 133] },
      margin: { top: startY, bottom: 35 },
      didDrawPage: generateHeader,
    });

    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.text(`Page ${i} of ${totalPages}`, pageWidth - 40, doc.internal.pageSize.height - 10);

      if (i === totalPages) {
        const pageHeight = doc.internal.pageSize.height;
        const footerY = pageHeight - 20;
        doc.text("Remarks:", 10, footerY - 10);
        doc.text(`Printed by: ${user.UserName}`, 10, footerY);
        doc.text(
          `Date Range: ${dateRange[0].format("DD-MM-YYYY HH:mm")} - ${dateRange[1].format("DD-MM-YYYY HH:mm")}`,
          10,
          footerY + 6
        );
        doc.text(`Printed on: ${dayjs().format("DD-MM-YYYY HH:mm:ss")}`, 10, footerY + 12);
      }
    }

    const pdfBlob = doc.output("blob");
    const formData = new FormData();
    formData.append("file", pdfBlob, `${reportConfig.filename}.pdf`);

    fetch(`${getApiBaseUrl()}/api/uploadReportCopy`, {
      method: "POST",
      body: formData,
    }).catch((err) => console.error("Upload failed:", err));

    doc.save(`${reportConfig.filename}.pdf`);
    setDownloadReportLoading(false);
  };

  const handlePasswordSubmit = async () => {
    if (!password) {
      setPasswordError("Please enter your password");
      return;
    }

    setAuthLoading(true);
    setPasswordError("");
    const username = user?.UserName || user?.Username;
    let loginSuccessful = false;

    try {
      const loginRes = await fetch(`${getApiBaseUrl()}${LOGIN}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });

      const loginData = await loginRes.json();
      if (loginData?.Message === "Login successful") {
        loginSuccessful = true;
      } else {
        setPasswordError("Reauthentication failed. Please check your password.");
      }
    } catch (error) {
      setPasswordError("Something went wrong. Please try again.");
    }

    try {
      const activityPayload = {
        ActivityType: 1,
        PerformedBy: user?.UserID,
        PerformedOn: dayjs().format("YYYY-MM-DD HH:mm:ss"),
        Notes: loginSuccessful
          ? `${reportConfig.title} downloaded by ${username}`
          : `Failed Reauthentication for ${reportConfig.title} by ${username}`,
        Location: "Reports",
        IsSucces: loginSuccessful ? 1 : 0,
      };

      await fetch(`${getApiBaseUrl()}${ADD_ACTIVITY}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(activityPayload),
      });
    } catch (err) {
      console.error("Activity log error:", err);
    } finally {
      setAuthLoading(false);
      if (loginSuccessful) {
        setVisible(false);
        setPassword("");
        handleExportPDF();
      }
    }
  };

  const handleUserChange = (value) => setSelectedUserId(value);

  const handleDateRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange(dates);
    } else {
      setDateRange([dayjs().subtract(7, "day"), dayjs()]);
    }
  };

  return (
    <>
      {downloadReportLoading && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            zIndex: 1050,
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(255, 255, 255, 0.2)",
          }}
        >
          <Spin size="large" tip="Generating PDF with all records..." />
        </div>
      )}
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/assetmgt/dashboard">
                        <i className="fas fa-home fa-lg" />
                      </Link>
                    </li>
                    <h4 className="breadcrumb-item" aria-current="page">
                      {reportConfig.title}
                    </h4>
                  </ol>
                </nav>
                <h6>Manage your audit reports</h6>
              </div>
            </div>
            <ul className="table-top-head">
              <li>
                <OverlayTrigger placement="top" overlay={renderTooltip}>
                  <Link
                    to="#"
                    onClick={() => {
                      setVisible(true);
                      setPassword("");
                      setPasswordError("");
                    }}
                  >
                    <ImageWithBasePath src="assets/img/icons/pdf.svg" alt="img" />
                  </Link>
                </OverlayTrigger>
              </li>
            </ul>
          </div>
          <div className="card table-list-card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <div className="search-set"></div>
              <div className="d-flex table-dropdown my-xl-auto right-content align-items-center flex-wrap gap-3">
                <div style={{ width: 200 }}>
                  <Select
                    style={{ width: "100%" }}
                    value={selectedUserId}
                    onChange={handleUserChange}
                    placeholder="Select Username"
                    showSearch
                    optionFilterProp="children"
                  >
                    <Option value={0}>All</Option>
                    {users?.map((u) => (
                      <Option key={u.UserID} value={u.UserID}>
                        {u.Username}
                      </Option>
                    ))}
                  </Select>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <RangePicker
                    showTime={{ format: "HH:mm" }}
                    format="DD-MM-YYYY HH:mm"
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    style={{ width: 350 }}
                  />
                  <Button
                    type="primary"
                    onClick={() => {
                      setCurrentPage(1);
                      fetchAuditLogData(1, pageSize);
                    }}
                    loading={loading}
                  >
                    Apply
                  </Button>
                </div>
              </div>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                {loading ? (
                  <div className="text-center my-4">
                    <Spin size="large" />
                    <p>Loading data...</p>
                  </div>
                ) : dataSource.length > 0 ? (
                  <Table
                    columns={tableColumns}
                    dataSource={dataSource}
                    currentPage={currentPage}
                    pageSize={pageSize}
                    totalRecords={totalRecords}
                    onPageChange={(page, size) => {
                      setCurrentPage(page);
                      setPageSize(size);
                    }}
                  />
                ) : (
                  <p className="text-center my-4">No data available</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <Modal
          title="Confirm Password"
          open={visible}
          onCancel={() => {
            setVisible(false);
            setPassword("");
            setPasswordError("");
          }}
          onOk={handlePasswordSubmit}
          confirmLoading={authLoading}
          okText="Verify & Download"
          width={350}
        >
          <div className="mb-3">
            <div className="pass-group">
              <input
                type={showPassword ? "text" : "password"}
                className={`form-control ${passwordError ? "is-invalid" : ""}`}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (passwordError) setPasswordError("");
                }}
                autoComplete="new-password"
                onCopy={(e) => e.preventDefault()}
                onPaste={(e) => e.preventDefault()}
                onCut={(e) => e.preventDefault()}
              />
              <span
                className={`ti toggle-password ${showPassword ? "ti-eye" : "ti-eye-off"}`}
                onClick={() => setShowPassword((prev) => !prev)}
              ></span>
            </div>
            {passwordError && (
              <div className="invalid-feedback" style={{ display: "block" }}>
                {passwordError}
              </div>
            )}
          </div>
        </Modal>
      </div>
    </>
  );
};

export default AuditLogReport;